import type { CrudFilter } from "@refinedev/core";

import { facetCounts } from "@/pages/areas/lib/area-hits";

/** The one field the group search reads. */
export const GROUP_QUERY_BY = "name";

export const GROUPS_PAGE_SIZE = 25;

/** How long the search box waits after a keystroke before the list asks again. */
export const SEARCH_DEBOUNCE_MS = 300;

/** "Made by Tina": a bool the transform derives, since Typesense cannot filter on a missing `externalKey`. */
export const MADE_BY_TINA_FIELD = "hasExternalKey";

/**
 * The `meta` of a group list query. It names `query` and `queryBy` every time: Refine folds the
 * url's own params into a list's meta, and the events table's search (`?query=rock&queryBy=...`)
 * must not reach the groups. Only a `meta` key set here wins over the url.
 */
export const groupExploreMeta = (
  term: string,
): { query: string; queryBy: string; facetBy: string[] } => ({
  query: term.trim() || "*",
  queryBy: GROUP_QUERY_BY,
  facetBy: [MADE_BY_TINA_FIELD],
});

/**
 * The filters of the group list. The entry is emitted on every render, `value: undefined` when
 * cleared: omitting it leaves a stale copy in Refine's filter state, and `transformFilter` drops
 * the blank ones.
 */
export const buildGroupFilters = (f: { madeByTina: boolean }): CrudFilter[] => [
  {
    field: MADE_BY_TINA_FIELD,
    operator: "eq",
    value: f.madeByTina ? true : undefined,
  },
];

/** The number of groups Tina made, from the `hasExternalKey` facet of a list answer, or null when it is not there. */
export const madeByTinaCount = (facets: unknown): number | null =>
  facetCounts(facets, MADE_BY_TINA_FIELD).find((c) => c.value === "true")
    ?.count ?? null;

export const groupCountLabel = (total: number): string =>
  total === 1 ? "1 group" : `${total} groups`;

/** What an empty list says, or null when it has rows. `total` counts every match, `term` is the search. */
export const emptyMessage = (
  rows: number,
  total: number,
  term: string,
): string | null => {
  if (rows > 0) return null;
  if (total === 0) {
    return term.trim() ? "No group matches that name." : "No event groups yet.";
  }
  return "No event groups on this page.";
};
